/**
 * BidScopeTemplates
 * -----------------
 * The sterilizer job and the water job come round again. Freeze a finished
 * record — division scope, BOM and commercial terms — into a template, then
 * drop it onto the next bid with every BOM line repriced off the live book.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LayoutTemplate, Save, Trash2, Download, Search, CheckCircle2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

export const BidScopeTemplates = ({ noteId, token, note, onApplied }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", tags: "",
    include_scope: true, include_bom: true, include_terms: true });
  const [applyFor, setApplyFor] = useState(null);
  const [applyOpts, setApplyOpts] = useState({ mode: "merge", apply_scope: true,
    apply_bom: true, apply_terms: true });
  const [lastApplied, setLastApplied] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bid-desk/scope-templates`, { headers, params: { q } });
      setTemplates(r.data?.templates || []);
    } catch { toast.error("Could not load the scope templates"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const divisionCount = (note?.scope_of_work?.divisions || []).length;
  const bomCount = (note?.bom || []).length;

  const saveTemplate = async () => {
    if (!form.name.trim()) { toast.error("Give the template a name"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/bid-desk/scope-templates`, {
        name: form.name.trim(),
        description: form.description.trim(),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        from_note_id: noteId,
        include_scope: form.include_scope,
        include_bom: form.include_bom,
        include_terms: form.include_terms,
      }, { headers });
      toast.success("Template saved", { description: "Drop it onto the next job from this panel" });
      setSaveOpen(false);
      setForm({ name: "", description: "", tags: "", include_scope: true, include_bom: true, include_terms: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the template");
    } finally { setBusy(false); }
  };

  const applyTemplate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bid-desk/notes/${noteId}/apply-template`, {
        template_id: applyFor.id, ...applyOpts }, { headers });
      setLastApplied(r.data.applied);
      toast.success(`"${applyFor.name}" applied`, {
        description: r.data.applied.repriced_from_book !== undefined
          ? `${r.data.applied.repriced_from_book} BOM line(s) repriced from the price book`
          : undefined });
      setApplyFor(null);
      onApplied?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply that template");
    } finally { setBusy(false); }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Delete the template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/bid-desk/scope-templates/${tpl.id}`, { headers });
      toast.success("Template deleted");
      load();
    } catch { toast.error("Could not delete that template"); }
  };

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="notes-templates">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-black text-slate-900 inline-flex items-center gap-2">
            <LayoutTemplate className="w-4 h-4 text-teal-600" /> Scope templates
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Save this division set, BOM and terms as a reusable package — or drop a saved one
            onto this record. BOM lines reprice off the live price book as they land.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates"
              className="h-8 pl-8 text-xs w-44" data-testid="template-search" />
          </div>
          <Button size="sm" variant="outline" onClick={() => setSaveOpen((v) => !v)}
            data-testid="template-save-open">
            <Save className="w-3.5 h-3.5 mr-1" /> Save this as a template
          </Button>
        </div>
      </div>

      {saveOpen && (
        <div className="mb-4 rounded-lg border border-teal-200 bg-teal-50/50 p-3" data-testid="template-save-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Sterilizer RO package — 4 unit SPD" className="h-9 text-xs"
              data-testid="template-name" />
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="Tags, comma separated (sterilizer, RO, SPD)" className="h-9 text-xs" />
          </div>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2} placeholder="When would you reach for this template?" className="mt-2 text-xs" />
          <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-slate-700">
            {[["include_scope", `Scope (${divisionCount} divisions)`],
              ["include_bom", `BOM (${bomCount} lines)`],
              ["include_terms", "Terms & markup defaults"]].map(([k, label]) => (
              <label key={k} className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                  data-testid={`template-${k}`} />
                {label}
              </label>
            ))}
            <Button size="sm" onClick={saveTemplate} disabled={busy}
              className="bg-teal-600 hover:bg-teal-700 ml-auto" data-testid="template-save-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save template
            </Button>
          </div>
        </div>
      )}

      {lastApplied && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
          data-testid="template-applied-banner">
          <CheckCircle2 className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
          <strong>{lastApplied.template}</strong> applied ({lastApplied.mode})
          {lastApplied.divisions !== undefined && ` · ${lastApplied.divisions} divisions`}
          {lastApplied.bom_lines !== undefined && ` · ${lastApplied.bom_lines} BOM lines`}
          {lastApplied.repriced_from_book !== undefined && ` · ${lastApplied.repriced_from_book} repriced from the book`}
          {!!lastApplied.unpriced && ` · ${lastApplied.unpriced} still at $0`}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["Template", "Carries", "Used", ""].map((h) => (
                <th key={h} className="text-left px-2 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {templates.map((t) => (
              <tr key={t.id} data-testid={`template-row-${t.id}`}>
                <td className="px-2 py-2">
                  <p className="font-bold text-slate-900 text-[13px]">{t.name}</p>
                  {t.description && <p className="text-[11px] text-slate-500">{t.description}</p>}
                  {!!(t.tags || []).length && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {t.tags.map((g) => (
                        <span key={g} className="text-[9.5px] font-bold uppercase tracking-wide text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5">{g}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-[11px] text-slate-600 whitespace-nowrap">
                  {t.division_count} divisions · {t.bom_count} BOM
                  {t.has_terms ? " · terms" : ""}
                  {t.markup_pct != null ? ` · ${t.markup_pct}% markup` : ""}
                </td>
                <td className="px-2 py-2 text-[11px] text-slate-500 whitespace-nowrap">{t.times_used}×</td>
                <td className="px-2 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="mr-1" data-testid={`template-apply-${t.id}`}
                    onClick={() => { setApplyFor(t); setApplyOpts({ mode: "merge", apply_scope: true, apply_bom: true, apply_terms: true }); }}>
                    <Download className="w-3.5 h-3.5 mr-1" /> Apply
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-red-600 hover:bg-red-50"
                    data-testid={`template-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {!templates.length && (
              <tr><td colSpan={4} className="px-2 py-4 text-sm text-slate-400">
                No templates yet — finish a scope, then save it here so the next job starts from it.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {applyFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setApplyFor(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}
            data-testid="template-apply-dialog">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-black text-slate-900">Apply “{applyFor.name}”</h4>
                <p className="text-xs text-slate-500">
                  {applyFor.division_count} divisions · {applyFor.bom_count} BOM lines
                </p>
              </div>
              <button onClick={() => setApplyFor(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex gap-2">
                {[["merge", "Merge into what's here"], ["replace", "Replace what's here"]].map(([m, label]) => (
                  <button key={m} onClick={() => setApplyOpts({ ...applyOpts, mode: m })}
                    className={`flex-1 rounded-md border px-3 py-2 text-xs font-semibold ${applyOpts.mode === m
                      ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600"}`}
                    data-testid={`template-mode-${m}`}>{label}</button>
                ))}
              </div>
              {[["apply_scope", "Division scope"], ["apply_bom", "BOM lines (repriced from the book)"],
                ["apply_terms", "Terms & markup defaults"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={applyOpts[k]}
                    onChange={(e) => setApplyOpts({ ...applyOpts, [k]: e.target.checked })}
                    data-testid={`template-opt-${k}`} />
                  {label}
                </label>
              ))}
              {applyOpts.mode === "replace" && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Replace overwrites the scope and BOM already on this record.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => setApplyFor(null)}>Cancel</Button>
              <Button size="sm" onClick={applyTemplate} disabled={busy}
                className="bg-teal-600 hover:bg-teal-700" data-testid="template-apply-confirm">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Apply template
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BidScopeTemplates;
